import classnames from 'classnames';
import React from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { UncontrolledTooltip } from 'reactstrap';
import CompleteReviewIcon from '@/shared/ui/icons/reviewed-icon.svg';
import './styles.scss';

type OverviewCircularProgressProps = {
  value?: number;
  name: string;
  index: number;
  isActive?: boolean;
  minDisplay?: number;
  minDisplayText?: number;
};

/**
 * Shows per-section review progress in a report overview as a circular gauge. Swaps in a completed-review icon once the value reaches 100, otherwise clamps small non-zero values up to minDisplay so they stay visible, and renders a tooltip with the reviewed percentage using a less-than form below minDisplayText.
 *
 * @param props - Progress props with value, name, index, isActive, minDisplay and minDisplayText.
 * @returns The rendered progress ring with its percentage tooltip, or the completed-review icon.
 */
const OverviewCircularProgress = ({
  value,
  name,
  index,
  isActive,
  minDisplay = 0,
  minDisplayText = 0,
}: OverviewCircularProgressProps) => {
  const formatValue = value || 0;
  return formatValue >= 100 ? (
    <img className="complete-review" src={CompleteReviewIcon} alt="Complete review icon" />
  ) : (
    <div id={`overview-${name}-${index}`} className={classnames('overview-circular-progress')}>
      <div className={classnames('circular-mask-border', isActive ? '--active' : '')} />
      <CircularProgressbar
        className="circular-progress-bar"
        value={formatValue > 0 && formatValue < minDisplay ? minDisplay : formatValue}
        strokeWidth={50}
        styles={buildStyles({
          strokeLinecap: 'butt',
          pathColor: isActive ? '#828C99' : '#C6CBD1',
          backgroundColor: '#FFFFFF',
          trailColor: '#FFFFFF',
        })}
      />
      <UncontrolledTooltip
        className="custom-tooltip"
        target={`overview-${name}-${index}`}
        placement="top"
        delay={{ show: 0, hide: 0 }}
        hideArrow
      >
        {`${formatValue > 0 && formatValue < minDisplayText ? `<${minDisplayText}` : formatValue}% reviewed`}
      </UncontrolledTooltip>
    </div>
  );
};

export default OverviewCircularProgress;
